import { compareMavenVersions, isUsableVersion } from "../maven-version";

/**
 * One event from an OSV affected range.
 *
 * `+x` introduced, `-x` fixed, `~x` last affected, `=x` an explicitly listed
 * version. The leading marker is used rather than a tuple so the compact
 * on-disk format stays a plain array of short strings.
 */
export type RangeEvent = string;

/** Chronological events for a single range, exactly as OSV published them. */
export type RangeEvents = RangeEvent[];

/**
 * Whether OSV's own ranges cover a version.
 *
 * This is the only implementation of the rule, and both the runtime matcher
 * and the offline dataset generator import it. They have to be the same rule:
 * a coordinate the generator expanded one way and the matcher evaluates
 * another produces silently wrong answers on exactly the data nobody can see.
 *
 * Events are applied in published order and never sorted. A lexicographic
 * sort moves "+2.0" ahead of "+1.0" and inverts whole ranges. A version is
 * affected if any single range covers it.
 */
export function rangesCoverVersion(
  ranges: RangeEvents[],
  version: string,
): boolean {
  for (const events of ranges) {
    let affected = false;
    for (const event of events) {
      const marker = event[0];
      const value = event.slice(1);
      if (marker === "=") {
        if (value === version) affected = true;
        continue;
      }
      if (marker === "+") {
        // OSV uses a bare 0 to mean "since the beginning of time".
        if (value === "0") {
          affected = true;
          continue;
        }
        if (isUsableVersion(value) && compareMavenVersions(version, value) >= 0) {
          affected = true;
        }
        continue;
      }
      if (marker === "-") {
        if (isUsableVersion(value) && compareMavenVersions(version, value) >= 0) {
          affected = false;
        }
        continue;
      }
      if (marker === "~") {
        if (isUsableVersion(value) && compareMavenVersions(version, value) > 0) {
          affected = false;
        }
      }
    }
    if (affected) return true;
  }
  return false;
}
